import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Shield, X, Check, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ContactInfoWarningModalProps {
	isOpen: boolean;
	onClose: () => void;
	onContinue: () => void;
	contactIssues: string[];
	userType: "parent" | "doula" | "admin";
}

const ContactInfoWarningModal: React.FC<ContactInfoWarningModalProps> = ({
	isOpen,
	onClose,
	onContinue,
	contactIssues,
	userType,
}) => {
	const getFilteringRules = () => [
		{
			icon: "📱",
			text: "Phone numbers will be replaced with ***-***-****",
			detected: contactIssues.includes("phone numbers"),
		},
		{
			icon: "📧",
			text: "Email addresses will be replaced with ***@***.***",
			detected: contactIssues.includes("email addresses"),
		},
		{
			icon: "🔗",
			text: "Website links will be replaced with ***link***",
			detected: contactIssues.includes("website links"),
		},
		{
			icon: "💬",
			text: "Contact phrases will be flagged for review",
			detected: contactIssues.includes("contact information"),
		},
	];

	const otherParty = userType === "parent" ? "doula" : "parent";

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
					{/* Backdrop */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="absolute inset-0 bg-black/60 backdrop-blur-sm"
						onClick={onClose}
					/>

					{/* Modal */}
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: 20 }}
						transition={{ type: "spring", duration: 0.3 }}
						className="relative w-full max-w-md mx-auto"
					>
						<Card className="glass-card border-0 shadow-2xl bg-white/95 backdrop-blur-xl">
							<CardHeader className="pb-4">
								<div className="flex items-start justify-between">
									<div className="flex items-center space-x-3">
										<div className="p-2 bg-amber-100 rounded-full">
											<AlertTriangle className="h-6 w-6 text-amber-600" />
										</div>
										<div>
											<CardTitle className="text-lg font-bold text-gray-900 font-karla">
												Platform Safety Notice
											</CardTitle>
											<p className="text-sm text-gray-600 font-karla">
												Contact information detected
											</p>
										</div>
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={onClose}
										className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
									>
										<X className="h-4 w-4" />
									</Button>
								</div>
							</CardHeader>

							<CardContent className="space-y-6">
								{/* Issues Detected */}
								<div>
									<h4 className="font-semibold text-gray-900 font-karla mb-3">
										Detected in your message:
									</h4>
									<div className="flex flex-wrap gap-2">
										{contactIssues.map((issue, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-red-100 text-red-800 border-red-200"
											>
												{issue}
											</Badge>
										))}
									</div>
								</div>

								{/* Safety Message */}
								<div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
									<div className="flex items-start space-x-3">
										<Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
										<div>
											<p className="text-sm text-blue-900 font-medium font-karla">
												Why we filter contact information
											</p>
											<p className="text-sm text-blue-800 font-karla mt-1">
												This helps protect both you and your {otherParty} from
												potential safety risks and keeps communication secure
												within our platform.
											</p>
										</div>
									</div>
								</div>

								{/* Filtering Rules */}
								<div>
									<h4 className="font-semibold text-gray-900 font-karla mb-3">
										If you continue, we will:
									</h4>
									<div className="space-y-2">
										{getFilteringRules().map((rule, index) => (
											<div
												key={index}
												className={`flex items-center space-x-3 p-2 rounded-lg ${
													rule.detected
														? "bg-amber-50 border border-amber-200"
														: "bg-gray-50"
												}`}
											>
												<span className="text-base">{rule.icon}</span>
												<span
													className={`text-sm font-karla flex-1 ${
														rule.detected
															? "text-amber-900 font-medium"
															: "text-gray-600"
													}`}
												>
													{rule.text}
												</span>
												{rule.detected && (
													<Check className="h-4 w-4 text-amber-600" />
												)}
											</div>
										))}
									</div>
								</div>

								{/* Action Buttons */}
								<div className="flex space-x-3 pt-4">
									<Button
										variant="outline"
										onClick={onClose}
										className="flex-1 font-karla"
									>
										<XCircle className="h-4 w-4 mr-2" />
										Cancel
									</Button>
									<Button
										onClick={onContinue}
										className="flex-1 bg-primary-500 hover:bg-primary-600 text-white font-karla"
									>
										<Check className="h-4 w-4 mr-2" />
										Send Message
									</Button>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default ContactInfoWarningModal;
